import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, Billboard, Text, Line, Html, Stars, Float } from '@react-three/drei';
import * as THREE from 'three';
import {
  MessageSquare, Bell, Mail, Brain, Globe, ArrowRight, User, Settings, Zap,
  Radio, Workflow, X, ToggleLeft, ToggleRight, Plus, Trash2, Save,
  MessageCircle, RefreshCw, Clock, Hand, Bot, Maximize2, Play, Pause,
  SkipForward, ChevronRight, Flame, Eye, Users, Activity, Search,
  ChevronDown, ChevronUp, ArrowDown, Expand, Shrink, Layers
} from 'lucide-react';
import { toast } from 'sonner';
import getApiUrl from '../../utils/getApiUrl';

const API = `${getApiUrl()}/api`;
const af = (url, o = {}) => {
  const t = localStorage.getItem("auth_token");
  return fetch(url, { ...o, credentials: "include", headers: { ...(o.headers || {}), ...(t ? { Authorization: `Bearer ${t}` } : {}) } });
};

const CH = { sms: { label: 'SMS', color: '#10B981' }, whatsapp: { label: 'WhatsApp', color: '#25D366' }, email: { label: 'Email', color: '#6366F1' }, notification: { label: 'Notif', color: '#F59E0B' }, christelle_ia: { label: 'IA', color: '#A855F7' }, webhook: { label: 'Webhook', color: '#06B6D4' }, update_field: { label: 'MAJ', color: '#64748B' } };
const TGT = { client: 'Client', telepro: 'Telepro', technicien: 'Tech.', manager: 'Manager', commercial: 'Com.', admin: 'Admin', all: 'Tous', system: 'Sys.' };
const ZONE_C = { telepro: '#3B82F6', r1: '#34C759', r2: '#F59E0B', chantiers: '#EF4444', administratif: '#8B5CF6', compta_fi_new: '#EC4899', amo: '#14B8A6', marketing: '#F97316', calendrier: '#06B6D4', tc_pac_iso: '#6366F1', aides_comp: '#EC4899' };
const TRIG_T = { auto: { label: 'Auto', color: '#10B981' }, semi_auto: { label: 'Semi', color: '#3B82F6' }, manual: { label: 'Manuel', color: '#F59E0B' }, conversational: { label: 'Conv', color: '#A855F7' } };

// BFS 3D layout with depth
const compute3DLayout = (stages) => {
  const tiers = {}; const visited = new Set();
  const queue = [{ id: 'lead', tier: 0 }];
  while (queue.length > 0) {
    const { id, tier } = queue.shift();
    if (visited.has(id)) continue; visited.add(id); tiers[id] = tier;
    const st = stages.find(s => s.stage_id === id);
    if (!st?.workflow?.exit_triggers) continue;
    for (const ex of st.workflow.exit_triggers)
      if (ex.target_stage && !visited.has(ex.target_stage)) queue.push({ id: ex.target_stage, tier: tier + 1 });
  }
  stages.forEach(s => { if (tiers[s.stage_id] === undefined) tiers[s.stage_id] = 6; });
  const groups = {};
  stages.forEach(s => { const t = tiers[s.stage_id]; if (!groups[t]) groups[t] = []; groups[t].push(s); });
  const pos = {};
  const maxTier = Math.max(...Object.keys(groups).map(Number));
  Object.entries(groups).forEach(([tier, stgs]) => {
    const t = Number(tier);
    const spread = stgs.length;
    stgs.forEach((s, i) => {
      const x = spread > 1 ? (i - (spread - 1) / 2) * 4.0 : 0;
      const y = (maxTier / 2 - t) * 3.8;
      const z = (i % 3 - 1) * 1.8;
      pos[s.stage_id] = [x, y, z];
    });
  });
  return pos;
};

const heatColor = (count, max) => {
  if (!count || max === 0) return null;
  const r = Math.min(count / max, 1);
  if (r < 0.15) return '#10B981';
  if (r < 0.35) return '#F59E0B';
  if (r < 0.6) return '#F97316';
  return '#EF4444';
};

const findPath = (stages, targetId) => {
  const visited = new Set(); const parent = {};
  const queue = ['lead']; visited.add('lead');
  while (queue.length > 0) {
    const curr = queue.shift();
    if (curr === targetId) break;
    const st = stages.find(s => s.stage_id === curr);
    if (!st?.workflow?.exit_triggers) continue;
    for (const ex of st.workflow.exit_triggers) {
      if (ex.target_stage && !visited.has(ex.target_stage)) { visited.add(ex.target_stage); parent[ex.target_stage] = curr; queue.push(ex.target_stage); }
    }
  }
  if (!parent[targetId] && targetId !== 'lead') return [];
  const path = []; let cur = targetId;
  while (cur) { path.unshift(cur); cur = parent[cur]; }
  return path;
};

// Hexagonal prism geometry creator
const createHexGeometry = (radius, height) => {
  const shape = new THREE.Shape();
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 3) * i - Math.PI / 6;
    const x = radius * Math.cos(angle);
    const y = radius * Math.sin(angle);
    if (i === 0) shape.moveTo(x, y);
    else shape.lineTo(x, y);
  }
  shape.closePath();
  const extrudeSettings = { depth: height, bevelEnabled: true, bevelThickness: 0.03, bevelSize: 0.03, bevelSegments: 2 };
  return new THREE.ExtrudeGeometry(shape, extrudeSettings);
};

// Animated ground grid
const GroundGrid = () => {
  const gridRef = useRef();
  useFrame((state) => {
    if (gridRef.current) {
      gridRef.current.material.opacity = 0.06 + Math.sin(state.clock.elapsedTime * 0.3) * 0.015;
    }
  });
  return (
    <group>
      <mesh ref={gridRef} rotation={[-Math.PI / 2, 0, 0]} position={[0, -4, 0]}>
        <planeGeometry args={[200, 200]} />
        <meshBasicMaterial color="#1e40af" transparent opacity={0.06} side={THREE.DoubleSide} />
      </mesh>
      <gridHelper args={[200, 80, '#1e3a5f', '#0d1b2a']} position={[0, -3.98, 0]} />
    </group>
  );
};

// Holographic ring at node base
const HoloRing = ({ color, radius, active, speed = 1 }) => {
  const ref = useRef();
  useFrame((state) => {
    if (!ref.current) return;
    ref.current.rotation.z = state.clock.elapsedTime * 0.4 * speed;
    const pulse = active ? 0.6 + Math.sin(state.clock.elapsedTime * 2) * 0.3 : 0.25;
    ref.current.material.opacity = pulse;
  });
  return (
    <mesh ref={ref} rotation={[-Math.PI / 2, 0, 0]}>
      <torusGeometry args={[radius, 0.025, 8, 64]} />
      <meshBasicMaterial color={color} transparent opacity={0.25} />
    </mesh>
  );
};

// GLOWING HEXAGONAL PILLAR NODE
const Node3D = ({ stage, position, leadCount, sourceData, heatmapOn, maxLeads, isSelected, isHighlighted, isSimCur, isDimmed, showGlobal, onClick }) => {
  const groupRef = useRef();
  const pillarRef = useRef();
  const capRef = useRef();
  const zone = stage.dashboards?.[0] || 'telepro';
  const baseColor = stage.stage_color || ZONE_C[zone] || '#3B82F6';
  const hc = heatmapOn && leadCount > 0 ? heatColor(leadCount, maxLeads) : null;
  const nodeColor = hc || baseColor;
  const wf = stage.workflow || {};
  const ea = wf.entry_actions || [];
  const ex = wf.exit_triggers || [];
  const hasIA = ea.some(a => a.type === 'christelle_ia');

  const pillarHeight = 0.4 + (leadCount > 0 ? Math.min(leadCount / Math.max(maxLeads, 1), 1) * 3.0 : 0);
  const hexRadius = 0.85;

  const hexGeo = useMemo(() => createHexGeometry(hexRadius, pillarHeight), [pillarHeight]);

  useFrame((state) => {
    if (groupRef.current) {
      const targetScale = isSelected ? 1.12 : (isSimCur ? 1.08 : 1);
      groupRef.current.scale.lerp(new THREE.Vector3(targetScale, targetScale, targetScale), 0.06);
    }
    if (pillarRef.current && !isDimmed) {
      pillarRef.current.position.y = -pillarHeight / 2 + Math.sin(state.clock.elapsedTime * 0.6 + position[0] * 0.5) * 0.06;
    }
    if (capRef.current) {
      const glow = isSimCur ? 1.2 : (isSelected ? 0.8 : 0.3);
      capRef.current.material.emissiveIntensity = glow + Math.sin(state.clock.elapsedTime * 1.5) * 0.15;
    }
  });

  const color3 = new THREE.Color(nodeColor);
  const darkerColor = new THREE.Color(nodeColor).multiplyScalar(0.4);

  return (
    <group position={position} ref={groupRef}>
      {/* Base holographic rings */}
      <group position={[0, -pillarHeight / 2 - 0.1, 0]}>
        {!isDimmed && <HoloRing color={nodeColor} radius={hexRadius + 0.25} active={isSelected || isSimCur} speed={1} />}
        {!isDimmed && (isSelected || isSimCur) && <HoloRing color={nodeColor} radius={hexRadius + 0.5} active={true} speed={-0.6} />}
      </group>

      {/* Subtle ground glow disc */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -pillarHeight / 2 - 0.08, 0]}>
        <circleGeometry args={[hexRadius + 0.6, 32]} />
        <meshBasicMaterial color={nodeColor} transparent opacity={isDimmed ? 0.01 : 0.08} />
      </mesh>

      {/* Main hexagonal pillar */}
      <mesh ref={pillarRef}
        geometry={hexGeo}
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, -pillarHeight / 2, 0]}
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        onPointerOver={() => { document.body.style.cursor = 'pointer'; }}
        onPointerOut={() => { document.body.style.cursor = 'default'; }}>
        <meshStandardMaterial
          color={isDimmed ? '#0a0f1e' : nodeColor}
          transparent
          opacity={isDimmed ? 0.06 : 0.85}
          emissive={isSimCur ? '#22D3EE' : (isSelected ? nodeColor : darkerColor)}
          emissiveIntensity={isDimmed ? 0 : (isSimCur ? 0.9 : (isSelected ? 0.6 : 0.2))}
          roughness={0.15}
          metalness={0.85}
        />
      </mesh>

      {/* Top cap - glowing hexagonal disc */}
      <mesh ref={capRef} rotation={[-Math.PI / 2, 0, 0]} position={[0, pillarHeight / 2 + 0.04, 0]}>
        <circleGeometry args={[hexRadius - 0.05, 6]} />
        <meshStandardMaterial
          color={isDimmed ? '#1e293b' : nodeColor}
          emissive={nodeColor}
          emissiveIntensity={isDimmed ? 0 : 0.3}
          transparent
          opacity={isDimmed ? 0.04 : 0.9}
          metalness={0.9}
          roughness={0.1}
        />
      </mesh>

      {/* Edge glow wireframe */}
      {!isDimmed && (
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, pillarHeight / 2 + 0.06, 0]}>
          <ringGeometry args={[hexRadius - 0.12, hexRadius - 0.02, 6]} />
          <meshBasicMaterial color={nodeColor} transparent opacity={isSelected ? 0.8 : 0.35} />
        </mesh>
      )}

      {/* Selection/sim pulsing ring */}
      {(isSelected || isSimCur || isHighlighted) && !isDimmed && (
        <Float speed={2} rotationIntensity={0} floatIntensity={0.3}>
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, pillarHeight / 2 + 0.3, 0]}>
            <torusGeometry args={[hexRadius + 0.1, 0.035, 8, 6]} />
            <meshBasicMaterial
              color={isSimCur ? '#22D3EE' : (isHighlighted ? '#A855F7' : nodeColor)}
              transparent opacity={0.85}
            />
          </mesh>
        </Float>
      )}

      {/* Point light for volumetric glow */}
      {!isDimmed && leadCount > 0 && (
        <pointLight
          position={[0, pillarHeight / 2 + 0.8, 0]}
          color={nodeColor}
          intensity={isSelected ? 4 : (isSimCur ? 5 : 1.2)}
          distance={5}
          decay={2}
        />
      )}

      {/* Beam of light above active nodes */}
      {!isDimmed && (isSelected || isSimCur) && (
        <mesh position={[0, pillarHeight / 2 + 2, 0]}>
          <cylinderGeometry args={[0.02, hexRadius * 0.3, 4, 8, 1, true]} />
          <meshBasicMaterial color={isSimCur ? '#22D3EE' : nodeColor} transparent opacity={0.08} side={THREE.DoubleSide} />
        </mesh>
      )}

      {/* HTML overlay label */}
      {!isDimmed && (
        <Billboard follow lockX={false} lockY={false} lockZ={false}>
          <Html center distanceFactor={7} position={[0, pillarHeight / 2 + 1.0, 0]} style={{ pointerEvents: 'none', userSelect: 'none' }}>
            <div className="flex flex-col items-center gap-0.5" style={{ width: '180px' }}>
              <div className="text-[12px] font-bold text-white text-center leading-tight whitespace-nowrap overflow-hidden text-ellipsis max-w-[170px]"
                style={{ textShadow: `0 0 16px ${nodeColor}90, 0 0 4px rgba(0,0,0,1)` }}>
                {stage.stage_name || stage.nom}
              </div>

              {leadCount > 0 && (
                <div className="flex items-center gap-1.5 rounded-full px-3 py-1"
                  style={{
                    background: `linear-gradient(135deg, ${nodeColor}30, rgba(0,0,0,0.85))`,
                    border: `1px solid ${hc || nodeColor}40`,
                    boxShadow: `0 0 12px ${hc || nodeColor}20`
                  }}>
                  <span className="text-[14px] font-black" style={{ color: hc || nodeColor, textShadow: `0 0 10px ${hc || nodeColor}80` }}>{leadCount}</span>
                  <span className="text-[9px] text-slate-300 font-medium">leads</span>
                </div>
              )}

              {showGlobal && ea.length > 0 && (
                <div className="flex items-center gap-0.5 mt-0.5">
                  {ea.filter(a => a.enabled !== false).slice(0, 4).map(a => {
                    const ch = CH[a.type];
                    return ch ? (
                      <div key={a.id} className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold"
                        style={{
                          background: `linear-gradient(135deg, ${ch.color}40, ${ch.color}15)`,
                          color: ch.color,
                          border: `1px solid ${ch.color}50`,
                          boxShadow: `0 0 6px ${ch.color}30`
                        }}>
                        {ch.label[0]}
                      </div>
                    ) : null;
                  })}
                  {hasIA && <div className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold" style={{ background: 'linear-gradient(135deg, #A855F740, #A855F715)', color: '#A855F7', border: '1px solid #A855F750' }}>IA</div>}
                </div>
              )}

              {showGlobal && sourceData?.sources?.length > 0 && (
                <div className="text-[8px] text-cyan-300/80 rounded px-2 py-0.5 max-w-[160px] truncate text-center"
                  style={{ background: 'linear-gradient(135deg, rgba(6,182,212,0.15), rgba(0,0,0,0.7))', border: '1px solid rgba(6,182,212,0.2)' }}>
                  {sourceData.sources.slice(0, 3).join(', ')}
                </div>
              )}

              <div className="text-[8px] text-slate-500 mt-0.5">{ex.length} sortie{ex.length > 1 ? 's' : ''}</div>
            </div>
          </Html>
        </Billboard>
      )}
    </group>
  );
};

// NEON CONNECTION with energy flow
const Connection3D = ({ from, to, color, isHighlighted, isSimConn, isDimmed, triggerType }) => {
  const mid = [(from[0] + to[0]) / 2, (from[1] + to[1]) / 2 + 1.5, (from[2] + to[2]) / 2];
  const trig = TRIG_T[triggerType];

  const tubeRef = useRef();

  useFrame((state) => {
    if (tubeRef.current && !isDimmed) {
      tubeRef.current.material.opacity = (isSimConn ? 0.7 : (isHighlighted ? 0.6 : 0.15)) + Math.sin(state.clock.elapsedTime * 2) * 0.05;
    }
  });

  const activeColor = isSimConn ? '#22D3EE' : (isHighlighted ? '#A855F7' : color);
  const lineWidth = isSimConn ? 4 : (isHighlighted ? 3 : 1.5);

  return (
    <group>
      {/* Glow line (wider, transparent) */}
      {!isDimmed && (
        <Line
          points={[from, mid, to]}
          color={activeColor}
          lineWidth={lineWidth + 3}
          transparent
          opacity={isSimConn ? 0.15 : (isHighlighted ? 0.1 : 0.04)}
        />
      )}
      {/* Core line */}
      <Line
        ref={tubeRef}
        points={[from, mid, to]}
        color={activeColor}
        lineWidth={lineWidth}
        transparent
        opacity={isDimmed ? 0.02 : (isSimConn ? 0.8 : (isHighlighted ? 0.65 : 0.2))}
        dashed={isDimmed}
        dashSize={0.3}
        gapSize={0.2}
      />
      {/* Trigger label */}
      {!isDimmed && trig && (
        <Billboard position={mid}>
          <Html center distanceFactor={14} style={{ pointerEvents: 'none' }}>
            <div className="px-2 py-0.5 rounded-md text-[7px] font-semibold backdrop-blur-sm"
              style={{
                background: `linear-gradient(135deg, rgba(0,0,0,0.9), ${trig.color}15)`,
                color: trig.color,
                border: `1px solid ${trig.color}35`,
                boxShadow: `0 0 8px ${trig.color}15`
              }}>
              {trig.label}
            </div>
          </Html>
        </Billboard>
      )}
    </group>
  );
};

// ENERGY PARTICLE with trail effect
const FlowParticle = ({ from, to, color, speed }) => {
  const ref = useRef();
  const trailRef = useRef();
  const mid = [(from[0] + to[0]) / 2, (from[1] + to[1]) / 2 + 1.5, (from[2] + to[2]) / 2];

  useFrame((state) => {
    if (!ref.current) return;
    const t = ((state.clock.elapsedTime * speed) % 1);

    // Quadratic bezier interpolation
    const inv = 1 - t;
    const px = inv * inv * from[0] + 2 * inv * t * mid[0] + t * t * to[0];
    const py = inv * inv * from[1] + 2 * inv * t * mid[1] + t * t * to[1];
    const pz = inv * inv * from[2] + 2 * inv * t * mid[2] + t * t * to[2];

    ref.current.position.set(px, py, pz);
    ref.current.scale.setScalar(0.6 + Math.sin(t * Math.PI) * 0.5);

    if (trailRef.current) {
      const tBack = Math.max(0, t - 0.04);
      const invB = 1 - tBack;
      trailRef.current.position.set(
        invB * invB * from[0] + 2 * invB * tBack * mid[0] + tBack * tBack * to[0],
        invB * invB * from[1] + 2 * invB * tBack * mid[1] + tBack * tBack * to[1],
        invB * invB * from[2] + 2 * invB * tBack * mid[2] + tBack * tBack * to[2]
      );
      trailRef.current.scale.setScalar(0.3 + Math.sin(t * Math.PI) * 0.2);
    }
  });

  return (
    <group>
      <mesh ref={ref}>
        <sphereGeometry args={[0.08, 8, 8]} />
        <meshBasicMaterial color={color} transparent opacity={0.95} />
      </mesh>
      <mesh ref={trailRef}>
        <sphereGeometry args={[0.06, 6, 6]} />
        <meshBasicMaterial color={color} transparent opacity={0.4} />
      </mesh>
    </group>
  );
};

// CAMERA CONTROLLER
const SceneController = ({ positions, target }) => {
  const { camera, gl } = useThree();
  const controlsRef = useRef();
  const fitted = useRef(false);

  const graphBounds = useMemo(() => {
    const allPos = Object.values(positions);
    if (allPos.length < 2) return null;
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    allPos.forEach(([x, y]) => { minX = Math.min(minX, x); maxX = Math.max(maxX, x); minY = Math.min(minY, y); maxY = Math.max(maxY, y); });
    const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
    const rangeY = maxY - minY;
    const camZ = (rangeY * 1.15) / 1.4;
    return { cx, cy, camZ: Math.max(camZ, 12) };
  }, [positions]);

  useEffect(() => {
    if (!graphBounds || fitted.current) return;
    fitted.current = true;
    const { cx, cy, camZ } = graphBounds;
    camera.position.set(cx + 4, cy - 2, camZ + 5);
    camera.lookAt(cx, cy, 0);
    camera.updateProjectionMatrix();
    if (controlsRef.current) { controlsRef.current.target.set(cx, cy, 0); controlsRef.current.update(); }
  }, [graphBounds, camera]);

  useEffect(() => {
    if (target) {
      const [x, y, z] = target;
      camera.position.set(x + 5, y + 3, z + 8);
      if (controlsRef.current) { controlsRef.current.target.set(x, y, z); controlsRef.current.update(); }
    }
  }, [target, camera]);

  return <OrbitControls ref={controlsRef} args={[camera, gl.domElement]} enableDamping dampingFactor={0.06} minDistance={3} maxDistance={120} />;
};

// DETAIL PANEL
const DetailPanel = ({ stage, sourceData, stages, toggle, del, onClose, editSec, setEditSec }) => {
  if (!stage) return null;
  const zone = stage.dashboards?.[0] || 'telepro';
  const color = stage.stage_color || ZONE_C[zone] || '#3B82F6';
  const wf = stage.workflow || {};
  const ea = wf.entry_actions || [];
  const et = wf.entry_triggers || [];
  const ex = wf.exit_triggers || [];
  const sd = sourceData || {};

  return (
    <div data-panel data-testid="detail-panel" className="absolute left-3 top-3 z-50 overflow-hidden animate-in slide-in-from-left-2" style={{
      width: 340,
      maxHeight: 'calc(100% - 24px)',
      background: 'linear-gradient(160deg, rgba(15,23,42,0.97), rgba(5,10,25,0.98))',
      backdropFilter: 'blur(24px)',
      border: `1px solid ${color}30`,
      borderRadius: '16px',
      boxShadow: `0 0 40px ${color}15, 0 20px 60px rgba(0,0,0,0.6)`
    }}>
      <div className="p-3 border-b border-slate-700/30 flex items-center justify-between" style={{ background: `linear-gradient(90deg, ${color}12, transparent)` }}>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color, boxShadow: `0 0 12px ${color}80` }} />
          <span className="text-sm font-bold text-white">{stage.stage_name || stage.nom}</span>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-slate-700/50 rounded-lg transition-colors"><X className="w-4 h-4 text-slate-400" /></button>
      </div>
      <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
        {(sd.sources?.length > 0 || sd.agents?.length > 0) && (
          <div className="p-3 border-b border-slate-800/40">
            {sd.sources?.length > 0 && (
              <div className="mb-2">
                <span className="text-[9px] text-cyan-500 uppercase tracking-wider font-semibold">Sources</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {sd.sources.map(s => <span key={s} className="text-[9px] px-1.5 py-0.5 rounded text-cyan-300" style={{ background: 'rgba(6,182,212,0.1)', border: '1px solid rgba(6,182,212,0.2)' }}>{s}</span>)}
                </div>
              </div>
            )}
            {sd.agents?.length > 0 && (
              <div>
                <span className="text-[9px] text-amber-500 uppercase tracking-wider font-semibold">Agents</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {sd.agents.map(a => <span key={a} className="text-[9px] px-1.5 py-0.5 rounded text-amber-300" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)' }}>{a}</span>)}
                </div>
              </div>
            )}
          </div>
        )}
        {[{ id: 'entry', key: 'entry_actions', label: "Actions d'entree", icon: Zap, items: ea, color: '#10B981' },
          { id: 'triggers', key: 'entry_triggers', label: 'Declencheurs', icon: Radio, items: et, color: '#3B82F6' },
          { id: 'exits', key: 'exit_triggers', label: 'Transitions de sortie', icon: ArrowRight, items: ex, color: '#F59E0B' },
        ].map(sec => {
          const isOpen = editSec === sec.id;
          return (
            <div key={sec.id} className="border-b border-slate-800/30 last:border-0">
              <button onClick={() => setEditSec(isOpen ? null : sec.id)} className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-white/[0.02] transition-colors">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded flex items-center justify-center" style={{ backgroundColor: `${sec.color}15` }}><sec.icon className="w-3 h-3" style={{ color: sec.color }} /></div>
                  <span className="text-xs font-semibold text-white">{sec.label}</span>
                  <span className="text-[10px] px-1.5 py-0.5 bg-slate-700/40 rounded-full text-slate-400">{sec.items.length}</span>
                </div>
                {isOpen ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
              </button>
              {isOpen && (
                <div className="px-3 pb-3 space-y-1.5">
                  {sec.items.length === 0 && <p className="text-[10px] text-slate-600 italic text-center py-2">Aucun element</p>}
                  {sec.items.map(item => {
                    const ch = CH[item.type] || CH.notification;
                    const tn = item.target_stage ? (stages.find(x => x.stage_id === item.target_stage)?.stage_name || item.target_stage) : null;
                    return (
                      <div key={item.id} className={`flex items-start gap-2 p-2 rounded-lg ${!item.enabled ? 'opacity-30' : ''}`}
                        style={{ background: 'rgba(15,23,42,0.5)', border: '1px solid rgba(51,65,85,0.2)' }}>
                        <div className="w-5 h-5 rounded flex-shrink-0 flex items-center justify-center mt-0.5" style={{ backgroundColor: `${ch.color}20` }}>
                          <span className="text-[8px] font-bold" style={{ color: ch.color }}>{ch.label[0]}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1 flex-wrap">
                            <span className="text-[10px] font-semibold" style={{ color: ch.color }}>{ch.label}</span>
                            {item.target && <span className="text-[8px] px-1 py-0.5 bg-slate-700/40 rounded text-slate-400">{TGT[item.target] || item.target}</span>}
                            {tn && <span className="text-[8px] text-amber-400 flex items-center gap-0.5"><ArrowRight className="w-2 h-2" />{tn}</span>}
                          </div>
                          <p className="text-[9px] text-slate-400 mt-0.5">{item.description || item.message || '-'}</p>
                        </div>
                        <div className="flex flex-col gap-0.5 flex-shrink-0">
                          <button onClick={() => toggle(stage.stage_id, sec.key, item.id)} className="p-0.5 hover:bg-slate-700/50 rounded">{item.enabled ? <ToggleRight className="w-3.5 h-3.5 text-emerald-400" /> : <ToggleLeft className="w-3.5 h-3.5 text-slate-500" />}</button>
                          <button onClick={() => del(stage.stage_id, sec.key, item.id)} className="p-0.5 hover:bg-red-500/20 rounded text-slate-600 hover:text-red-400"><Trash2 className="w-2.5 h-2.5" /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// SIM PANEL
const SimPanel = ({ simPath, simStep, merged, onStep, onPlay, onStop, playing }) => {
  const cur = simPath[simStep]; const stage = merged.find(s => s.stage_id === cur);
  const wf = stage?.workflow || {}; const ea = wf.entry_actions || []; const next = simPath[simStep + 1];
  return (
    <div data-testid="sim-panel" className="absolute bottom-3 left-1/2 -translate-x-1/2 z-50 shadow-2xl" style={{
      width: 420,
      background: 'linear-gradient(160deg, rgba(15,23,42,0.97), rgba(5,10,25,0.98))',
      backdropFilter: 'blur(24px)',
      border: '1px solid rgba(34,211,238,0.3)',
      borderRadius: '16px',
      padding: '14px',
      boxShadow: '0 0 40px rgba(34,211,238,0.1), 0 20px 60px rgba(0,0,0,0.5)'
    }}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-cyan-400 animate-pulse" /><span className="text-xs font-bold text-cyan-300">Simulation</span>
          <span className="text-[10px] text-slate-500">Etape {simStep + 1}/{simPath.length}</span>
        </div>
        <button onClick={onStop} className="p-1 hover:bg-slate-700/50 rounded-lg"><X className="w-3.5 h-3.5 text-slate-400" /></button>
      </div>
      <div className="w-full h-1 rounded-full mb-2 overflow-hidden" style={{ background: 'rgba(30,41,59,0.8)' }}>
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${((simStep + 1) / simPath.length) * 100}%`, background: 'linear-gradient(90deg, #22D3EE, #A855F7)' }} />
      </div>
      <div className="rounded-lg p-2 mb-2 flex items-start gap-3" style={{ background: 'rgba(30,41,59,0.5)', border: '1px solid rgba(34,211,238,0.15)' }}>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stage?.stage_color || '#3B82F6', boxShadow: `0 0 8px ${stage?.stage_color || '#3B82F6'}` }} />
            <span className="text-[12px] font-bold text-white">{stage?.stage_name || cur}</span>
          </div>
          {ea.filter(a => a.enabled !== false).length > 0 && (
            <div className="flex flex-wrap gap-1 mb-1">
              {ea.filter(a => a.enabled !== false).map(a => {
                const ch = CH[a.type]; return ch ? <span key={a.id} className="text-[8px] px-1.5 py-0.5 rounded" style={{ backgroundColor: `${ch.color}15`, color: ch.color }}>{ch.label} - {TGT[a.target] || a.target}</span> : null;
              })}
            </div>
          )}
        </div>
        {next && <div className="text-right"><span className="text-[8px] text-amber-500 block">Vers :</span><span className="text-[10px] text-amber-300">{merged.find(s => s.stage_id === next)?.stage_name || next}</span></div>}
        {!next && simStep === simPath.length - 1 && <span className="text-[10px] text-emerald-400 font-semibold">Fin</span>}
      </div>
      <div className="flex items-center gap-1.5 justify-center">
        <button disabled={simStep === 0} onClick={() => onStep(-1)} className="px-2 py-1 rounded-lg text-[10px] text-white disabled:opacity-30 transition-colors" style={{ background: 'rgba(51,65,85,0.6)' }}><ChevronRight className="w-3 h-3 rotate-180 inline" /> Prec.</button>
        <button onClick={onPlay} className="px-3 py-1 rounded-lg text-[10px] text-white transition-colors" style={{ background: playing ? 'rgba(34,211,238,0.5)' : 'rgba(34,211,238,0.7)' }}>{playing ? <><Pause className="w-3 h-3 inline" /> Pause</> : <><Play className="w-3 h-3 inline" /> Auto</>}</button>
        <button disabled={simStep >= simPath.length - 1} onClick={() => onStep(1)} className="px-2 py-1 rounded-lg text-[10px] text-white disabled:opacity-30 transition-colors" style={{ background: 'rgba(51,65,85,0.6)' }}>Suiv. <SkipForward className="w-3 h-3 inline" /></button>
      </div>
    </div>
  );
};

// MAIN COMPONENT
const WorkflowCockpit3D = () => {
  const [stages, setStages] = useState([]);
  const [etapes, setEtapes] = useState([]);
  const [dashboards, setDashboards] = useState({});
  const [loading, setLoading] = useState(true);
  const [leadCounts, setLeadCounts] = useState({});
  const [leadSources, setLeadSources] = useState({});
  const [topSources, setTopSources] = useState([]);
  const [heatmapOn, setHeatmapOn] = useState(false);
  const [showGlobal, setShowGlobal] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [editSec, setEditSec] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightPath, setHighlightPath] = useState(new Set());
  const [simPath, setSimPath] = useState([]);
  const [simStep, setSimStep] = useState(0);
  const [simPlaying, setSimPlaying] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [focusTarget, setFocusTarget] = useState(null);
  const containerRef = useRef(null);
  const simTimerRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sr, cr, dr, lc, ls] = await Promise.all([
        af(`${API}/pipeline-workflow/stages`).then(r => r.json()),
        af(`${API}/config/pipeline/all`).then(r => r.json()),
        af(`${API}/config/dashboards`).then(r => r.json()),
        af(`${API}/pipeline-workflow/lead-counts`).then(r => r.json()),
        af(`${API}/pipeline-workflow/lead-sources`).then(r => r.json()),
      ]);
      setStages(sr.stages || []); setEtapes(cr.etapes || []); setDashboards(dr.dashboards || {});
      setLeadCounts(lc.counts || {}); setLeadSources(ls.stages || {}); setTopSources(ls.top_sources || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const interval = setInterval(async () => {
      try { const lc = await af(`${API}/pipeline-workflow/lead-counts`).then(r => r.json()); setLeadCounts(lc.counts || {}); } catch {}
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const merged = useMemo(() => stages.map(s => {
    const e = etapes.find(p => p.id === s.stage_id) || {};
    const db = e.dashboards?.length > 0 ? e.dashboards : (e.dashboard ? [e.dashboard] : ['telepro']);
    return { ...s, ...e, dashboards: db, workflow: s.workflow };
  }), [stages, etapes]);

  const positions = useMemo(() => compute3DLayout(merged), [merged]);
  const maxLeadCount = useMemo(() => Math.max(...Object.values(leadCounts), 1), [leadCounts]);
  const totalLeads = useMemo(() => Object.values(leadCounts).reduce((a, b) => a + b, 0), [leadCounts]);

  const conns = useMemo(() => {
    const arr = [];
    merged.forEach(s => {
      const p1 = positions[s.stage_id]; if (!p1) return;
      (s.workflow?.exit_triggers || []).forEach(ex => {
        if (!ex.target_stage) return;
        const p2 = positions[ex.target_stage]; if (!p2) return;
        const zone = s.dashboards?.[0] || 'telepro';
        arr.push({ from: s.stage_id, to: ex.target_stage, p1, p2, color: ZONE_C[zone] || '#475569', triggerType: ex.type || 'semi_auto', enabled: ex.enabled !== false });
      });
    });
    return arr;
  }, [merged, positions]);

  useEffect(() => {
    if (!searchQuery.trim()) { setHighlightPath(new Set()); setFocusTarget(null); return; }
    const q = searchQuery.toLowerCase();
    const match = merged.find(s => (s.stage_name || s.nom || '').toLowerCase().includes(q) || s.stage_id.includes(q));
    if (match) { setHighlightPath(new Set(findPath(merged, match.stage_id))); setFocusTarget(positions[match.stage_id] || null); }
    else { setHighlightPath(new Set()); setFocusTarget(null); }
  }, [searchQuery, merged, positions]);

  useEffect(() => {
    if (simPlaying && simPath.length > 0) {
      simTimerRef.current = setInterval(() => {
        setSimStep(prev => { if (prev >= simPath.length - 1) { setSimPlaying(false); return prev; } return prev + 1; });
      }, 1800);
    }
    return () => { if (simTimerRef.current) clearInterval(simTimerRef.current); };
  }, [simPlaying, simPath]);

  useEffect(() => {
    if (simPath.length > 0 && positions[simPath[simStep]]) setFocusTarget(positions[simPath[simStep]]);
  }, [simStep, simPath, positions]);

  const saveWf = async (sid, wf) => { try { await af(`${API}/pipeline-workflow/stages/${sid}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ stage_id: sid, workflow: wf }) }); setStages(p => p.map(s => s.stage_id === sid ? { ...s, workflow: wf } : s)); toast.success('OK'); } catch { toast.error('Erreur'); } };
  const toggle = (sid, sec, aid) => { const s = stages.find(x => x.stage_id === sid); if (!s) return; const wf = { ...s.workflow }; wf[sec] = wf[sec].map(i => i.id === aid ? { ...i, enabled: !i.enabled } : i); saveWf(sid, wf); };
  const del = (sid, sec, aid) => { const s = stages.find(x => x.stage_id === sid); if (!s) return; const wf = { ...s.workflow }; wf[sec] = wf[sec].filter(i => i.id !== aid); saveWf(sid, wf); };

  const startSim = useCallback(() => {
    const path = ['lead']; const visited = new Set(['lead']); let cur = 'lead';
    for (let i = 0; i < 30; i++) {
      const st = merged.find(s => s.stage_id === cur);
      if (!st?.workflow?.exit_triggers) break;
      const next = st.workflow.exit_triggers.find(e => e.enabled !== false && e.target_stage && !visited.has(e.target_stage));
      if (!next) break;
      visited.add(next.target_stage); path.push(next.target_stage); cur = next.target_stage;
    }
    setSimPath(path); setSimStep(0); setSimPlaying(false);
    toast.success(`Simulation : ${path.length} etapes`);
  }, [merged]);
  const stopSim = useCallback(() => { setSimPath([]); setSimStep(0); setSimPlaying(false); setFocusTarget(null); }, []);

  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current; if (!el) return;
    if (!isFullscreen) { if (el.requestFullscreen) el.requestFullscreen(); else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen(); }
    else { if (document.exitFullscreen) document.exitFullscreen(); else if (document.webkitExitFullscreen) document.webkitExitFullscreen(); }
    setIsFullscreen(!isFullscreen);
  }, [isFullscreen]);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#A855F7', borderTopColor: 'transparent' }} />
        <span className="text-xs text-slate-500 font-medium">Chargement du cockpit...</span>
      </div>
    </div>
  );

  const isSimActive = simPath.length > 0;
  const simPathSet = new Set(simPath);
  const simConnSet = new Set(simPath.slice(0, -1).map((s, i) => `${s}->${simPath[i + 1]}`));
  const selectedStage = merged.find(s => s.stage_id === selectedNode);

  return (
    <div ref={containerRef} data-testid="cockpit-ecosystem" className={`flex flex-col ${isFullscreen ? '' : ''}`} style={{ height: isFullscreen ? '100vh' : undefined }}>
      {/* TOOLBAR */}
      <div style={{
        background: 'linear-gradient(160deg, rgba(15,23,42,0.95), rgba(10,15,30,0.95))',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(148,163,184,0.12)',
        borderRadius: isFullscreen ? 0 : '16px',
        padding: '14px',
        flexShrink: 0
      }}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-bold text-white flex items-center gap-2">
              <Workflow className="w-4 h-4" style={{ color: '#A855F7' }} /> Cockpit 3D
            </h2>
            <span className="text-[10px] text-slate-500">{merged.length} etapes - {conns.length} branches</span>
            {totalLeads > 0 && (
              <span className="text-[10px] flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ color: '#22D3EE', background: 'rgba(34,211,238,0.08)', border: '1px solid rgba(34,211,238,0.15)' }}>
                <Users className="w-3 h-3" />{totalLeads} leads
              </span>
            )}
            {topSources.length > 0 && (
              <span className="text-[10px] flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ color: '#10B981', background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.15)' }}>
                <Layers className="w-3 h-3" />{topSources.length} sources
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={load} data-testid="cockpit-refresh" className="p-1.5 rounded-lg text-slate-400 hover:text-white transition-colors" style={{ background: 'rgba(51,65,85,0.4)' }}>
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
            <button data-testid="fullscreen-toggle" onClick={toggleFullscreen} className="p-1.5 rounded-lg text-slate-400 hover:text-white transition-colors" style={{ background: 'rgba(51,65,85,0.4)' }}>
              {isFullscreen ? <Shrink className="w-3.5 h-3.5" /> : <Expand className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="heatmap-toggle" onClick={() => setHeatmapOn(h => !h)}
            className="px-2.5 py-1.5 rounded-lg text-[10px] font-medium flex items-center gap-1 transition-all"
            style={{
              background: heatmapOn ? 'rgba(234,88,12,0.6)' : 'rgba(30,41,59,0.6)',
              color: heatmapOn ? '#fff' : '#94a3b8',
              border: `1px solid ${heatmapOn ? 'rgba(234,88,12,0.4)' : 'rgba(51,65,85,0.3)'}`
            }}>
            <Flame className="w-3 h-3" />Heatmap
          </button>
          <button data-testid="global-toggle" onClick={() => setShowGlobal(g => !g)}
            className="px-2.5 py-1.5 rounded-lg text-[10px] font-medium flex items-center gap-1 transition-all"
            style={{
              background: showGlobal ? 'rgba(16,185,129,0.6)' : 'rgba(30,41,59,0.6)',
              color: showGlobal ? '#fff' : '#94a3b8',
              border: `1px solid ${showGlobal ? 'rgba(16,185,129,0.4)' : 'rgba(51,65,85,0.3)'}`
            }}>
            <Eye className="w-3 h-3" />Vue Globale
          </button>
          <button data-testid="sim-start" onClick={isSimActive ? stopSim : startSim}
            className="px-2.5 py-1.5 rounded-lg text-[10px] font-medium flex items-center gap-1 transition-all"
            style={{
              background: isSimActive ? 'rgba(34,211,238,0.6)' : 'rgba(30,41,59,0.6)',
              color: isSimActive ? '#fff' : '#94a3b8',
              border: `1px solid ${isSimActive ? 'rgba(34,211,238,0.4)' : 'rgba(51,65,85,0.3)'}`,
              animation: isSimActive ? 'pulse 2s infinite' : 'none'
            }}>
            {isSimActive ? <><Pause className="w-3 h-3" />Stop</> : <><Play className="w-3 h-3" />Simuler</>}
          </button>
          <div className="w-px h-5" style={{ background: 'rgba(51,65,85,0.5)' }} />
          <div className="relative">
            <Search className="w-3 h-3 text-slate-500 absolute left-2 top-1/2 -translate-y-1/2" />
            <input data-testid="cockpit-search" type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Rechercher..."
              className="pl-6 pr-6 py-1.5 rounded-lg text-[10px] text-white w-44 outline-none"
              style={{ background: 'rgba(30,41,59,0.6)', border: '1px solid rgba(51,65,85,0.3)' }} />
            {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-1.5 top-1/2 -translate-y-1/2"><X className="w-3 h-3 text-slate-500" /></button>}
          </div>
          {highlightPath.size > 0 && <span className="text-[10px]" style={{ color: '#A855F7' }}>{highlightPath.size} etapes</span>}
          {topSources.length > 0 && (
            <div className="flex items-center gap-1 ml-2">
              {topSources.slice(0, 4).map(s => (
                <span key={s.source} className="text-[8px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.15)', color: '#6ee7b7' }}>
                  {s.source} ({s.count})
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 3D CANVAS */}
      <div className={`relative ${isFullscreen ? '' : 'mt-3'} overflow-hidden`}
        style={{
          height: isFullscreen ? '100vh' : 'calc(100vh - 230px)',
          borderRadius: isFullscreen ? 0 : '16px',
          border: isFullscreen ? 'none' : '1px solid rgba(148,163,184,0.08)'
        }}>
        <Canvas
          camera={{ position: [0, -2, 30], fov: 55 }}
          style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}
          gl={{ antialias: true, alpha: false }}
          onCreated={({ gl }) => {
            gl.setClearColor('#020612');
            gl.toneMapping = THREE.ACESFilmicToneMapping;
            gl.toneMappingExposure = 1.2;
          }}
        >
          {/* Ambient fill */}
          <ambientLight intensity={0.15} color="#e0e7ff" />

          {/* Key light - warm from top right */}
          <pointLight position={[20, 20, 15]} intensity={1.2} color="#f8fafc" />

          {/* Fill light - cool purple from bottom left */}
          <pointLight position={[-15, -12, -10]} intensity={0.5} color="#7c3aed" />

          {/* Accent light - blue from front */}
          <pointLight position={[0, -15, 10]} intensity={0.4} color="#3b82f6" />

          {/* Rim light - cyan accent */}
          <pointLight position={[0, 10, -15]} intensity={0.3} color="#22d3ee" />

          {/* Deep space starfield */}
          <Stars radius={150} depth={80} count={4000} factor={3.5} saturation={0.2} fade speed={0.3} />

          {/* Nebula fog */}
          <fog attach="fog" args={['#050a15', 40, 120]} />

          {/* Ground grid */}
          <GroundGrid />

          {/* Connections */}
          {conns.map((c, i) => {
            const isHL = highlightPath.size > 0 && highlightPath.has(c.from) && highlightPath.has(c.to);
            const isSC = isSimActive && simConnSet.has(`${c.from}->${c.to}`);
            const dimmed = (highlightPath.size > 0 && !isHL) || (isSimActive && !isSC);
            return (
              <React.Fragment key={i}>
                <Connection3D from={c.p1} to={c.p2} color={c.color} isHighlighted={isHL} isSimConn={isSC} isDimmed={dimmed} triggerType={c.triggerType} />
                {c.enabled && !dimmed && <FlowParticle from={c.p1} to={c.p2} color={isSC ? '#22D3EE' : c.color} speed={0.1 + (i % 5) * 0.03} />}
              </React.Fragment>
            );
          })}

          {/* Nodes */}
          {merged.map(stage => {
            const p = positions[stage.stage_id]; if (!p) return null;
            const isHL = highlightPath.has(stage.stage_id);
            const isSim = isSimActive && simPathSet.has(stage.stage_id);
            const isSimCur = isSimActive && simPath[simStep] === stage.stage_id;
            const dimmed = (highlightPath.size > 0 && !isHL) || (isSimActive && !isSim);
            return (
              <Node3D key={stage.stage_id} stage={stage} position={p}
                leadCount={leadCounts[stage.stage_id] || 0} sourceData={leadSources[stage.stage_id]}
                heatmapOn={heatmapOn} maxLeads={maxLeadCount}
                isSelected={selectedNode === stage.stage_id} isHighlighted={isHL}
                isSimCur={isSimCur} isDimmed={dimmed} showGlobal={showGlobal}
                onClick={() => { setSelectedNode(selectedNode === stage.stage_id ? null : stage.stage_id); setEditSec(null); }}
              />
            );
          })}

          <SceneController positions={positions} target={focusTarget} />
        </Canvas>

        {selectedStage && (
          <DetailPanel stage={selectedStage} sourceData={leadSources[selectedStage.stage_id]}
            stages={stages} toggle={toggle} del={del}
            onClose={() => setSelectedNode(null)} editSec={editSec} setEditSec={setEditSec} />
        )}

        {isSimActive && (
          <SimPanel simPath={simPath} simStep={simStep} merged={merged}
            onStep={(d) => setSimStep(p => Math.max(0, Math.min(simPath.length - 1, p + d)))}
            onPlay={() => setSimPlaying(p => !p)} onStop={stopSim} playing={simPlaying} />
        )}
      </div>
    </div>
  );
};

export default WorkflowCockpit3D;
